'use client';

import { useCallback, useMemo } from 'react';
import { UserProfile, useUser } from '@auth0/nextjs-auth0/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { isEmpty } from 'lodash';
import { CurrentUser } from '@/server/services/Auth0/Auth0.types';
import { AUTH0_PATH, LOGIN_HINT_PARAM, REDIRECT_URL_PARAM } from '../../constants/pages';
import { Api, isAxiosError } from '../api';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

// Shared in-flight promise to coalesce concurrent updateSession calls
let sharedUpdateSessionPromise: Promise<unknown> | null = null;

export const useNextAuth = () => {
  const router = useRouter();
  const params = useSearchParams();
  const { user, isLoading, checkSession } = useUser();

  const status = useMemo<Status>(() => {
    if (isLoading) return 'loading';
    return user ? 'authenticated' : 'unauthenticated';
  }, [isLoading, user]);

  const session = useMemo(() => {
    return user ? ({ user } as { user: CurrentUser }) : undefined;
  }, [user]);

  const logout = useCallback(async () => {
    const loginHint = params?.get(LOGIN_HINT_PARAM);
    if (loginHint && status !== 'authenticated') {
      const loginUrl = Api.user.getLoginPath({ loginHint });
      router.push(loginUrl);
    } else {
      router.push(AUTH0_PATH.LOGOUT);
    }
  }, [router, params, status]);

  const refreshToken = useCallback(() => {
    Api.user.refreshToken().catch((err) => {
      if (isAxiosError(err) && err.response?.status === 403) {
        router.push(AUTH0_PATH.LOGOUT);
      }
    });
  }, [router]);

  const updateSession = useCallback(() => {
    if (!sharedUpdateSessionPromise) {
      sharedUpdateSessionPromise = Api.user
        .updateSession()
        .then(() => checkSession())
        .finally(() => {
          sharedUpdateSessionPromise = null;
        });
    }

    return sharedUpdateSessionPromise.catch((err) => {
      if (isAxiosError(err) && err.response?.status === 403) {
        router.push(AUTH0_PATH.LOGOUT);
      }
    });
  }, [checkSession, router]);

  const loginToOrganization = useCallback(
    ({ org_id }: Pick<UserProfile, 'org_id'>) => {
      if (org_id !== user?.org_id) {
        const url = Api.user.getLoginPathToOrganization({
          org_id: org_id!,
          [REDIRECT_URL_PARAM]: window.location.pathname,
        });
        router.push(url);
      }
    },
    [user, router],
  );

  return {
    isLoggedIn: !isEmpty(user),
    isEmailVerified: user?.email_verified || false,
    session,
    status,
    logout,
    refreshToken,
    updateSession,
    checkSession,
    loginToOrganization,
  };
};
